import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { fetchWithAuth, postWithUsername } from "../services/fetchWrapper";
import { useAuth } from "../utils/AuthContext";
import { Environment } from "../utils/Environment";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Collapse from "@mui/material/Collapse";
import DeleteIcon from "@mui/icons-material/Delete";

const apiUrl = Environment.getApiUrl();

const UploadService: React.FC = () => {
  const { isLoggedIn, userName } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState({
    layer_name: "",
    table_passed: "",
    client_id: "",
    client_name: "",
    job_id: "",
    create_table_name: "",
    main_table_name: "",
    srid: 0,
    combine_table_flag: "false"
  });
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [isLayerNameRequired, setIsLayerNameRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  const [uploadedLayers, setUploadedLayers] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCombineBoxShown, setIsCombineBoxShown] = useState(false);
  const [showCombineHelp, setShowCombineHelp] = useState(true);
  const [uploadedFileNames, setUploadedFileNames] = useState<{ [key: string]: string[] }>({});
  const [failedLayers, setFailedLayers] = useState<string[]>([]);
  const pendingUploadTableRef = useRef<string | null>(null);
  const tableRowRefs = useRef<Record<string, HTMLLIElement | null>>({});
  let uploadedFiles = Array.from(selectedFiles || []).map(f => f.name);
  // const [statusLoading, setStatusLoading] = useState(false);

  const REQUIRED_TABLES = [
    "flowlines",
    "structures",
    "wetlands",
    "developed",
    "concrete",
    "steel",
    "allwater",
    "roadways",
    "railways"
  ];

  const DISPLAY_NAMES: Record<string, string> = {
    allwater: "All Water",
    flowlines: "Flowlines",
    structures: "Structures",
    wetlands: "Wetlands",
    developed: "Developed",
    concrete: "Concrete",
    steel: "Steel",
    roadways: "Roadways",
    railways: "Railways",
  };

  const combineSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isCombineBoxShown) {
      combineSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isCombineBoxShown]);

  useEffect(() => {
    if (!form.create_table_name) return;

    const activeRow = tableRowRefs.current[form.create_table_name];
    if (activeRow) {
      activeRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [form.create_table_name, isLayerNameRequired]);

  // const availableTables = REQUIRED_TABLES.filter(
  //   (table) => !uploadedLayers.includes(table)
  // );

  useEffect(() => {
    // Restore uploadedLayers from localStorage keys
    const layers = REQUIRED_TABLES.filter(table =>
      localStorage.getItem(`uploadedCompareFile_${table}`)
    );
    setUploadedLayers(layers);

    // Restore uploadedFileNames from localStorage values
    const fileNames: { [key: string]: string[] } = {};
    layers.forEach(table => {
      const names = localStorage.getItem(`uploadedCompareFile_${table}`);
      if (names) fileNames[table] = names.split(", ");
    });
    setUploadedFileNames(fileNames);
  }, []);

  //Upload dialogue box logic
  useEffect(() => {
    if (uploading || (pollingJobId && (!uploadStatus || uploadStatus === "processing"))) {
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
    }
  }, [uploading, pollingJobId, uploadStatus]);

  // Poll for status using job_id
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let initialInterval = 3000; // Start with 3 seconds
    let maxInterval = 30000;    // Max out at 30 seconds
    let currentInterval = initialInterval;

    const pollStatus = async () => {
      try {
        const response = await fetch(`${apiUrl}/upload-status/${pollingJobId}`);
        if (response.ok) {
          const data = await response.json();
          setUploadStatus(data.upload_status);
          setStatusMessage(data.message);
          if (
            ["failed", "unknown", "error"].includes(data.upload_status)
          ) {
            setPollingJobId(null); // Stop polling
            if (pendingUploadTableRef.current) {
              const failedTable = pendingUploadTableRef.current;
              //Clear successful upload status for failed table
              handleClearUploaded(failedTable);
              setFailedLayers((prev) =>
                prev.includes(failedTable)
                  ? prev
                  : [...prev, failedTable]
              );
              pendingUploadTableRef.current = null;
            }
            return;
          }
          if (
            ["completed", "success"].includes(data.upload_status)
          ) {
            setUploadedLayers((prev) => {
              // Avoid duplicates
              if (!prev.includes(form.create_table_name)) {
                return [...prev, form.create_table_name];
              }
              return prev;
            });
            // make sure successfully uploaded table is removed from fail list if it was previously there
            setFailedLayers((prev) => prev.filter((t) => t !== form.create_table_name));
            pendingUploadTableRef.current = null;
            setPollingJobId(null); // Stop polling
            return;
          }
          // If still processing, increase interval (exponential backoff)
          currentInterval = Math.min(currentInterval * 2, maxInterval);
        } else {
          setStatusMessage("Failed to get upload status.");
        }
      } catch (err) {
        setStatusMessage("Error polling upload status.");
      }
      // Schedule next poll
      timeout = setTimeout(pollStatus, currentInterval);
    };

    if (pollingJobId) {
      currentInterval = initialInterval; // Reset interval for new job
      pollStatus();
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [pollingJobId, apiUrl]);

  // Fetch client data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await new Promise(res => setTimeout(res, 2000));
      try {
        const username = localStorage.getItem("userName");
        if (!username) {
          setLoading(false);
          return;
        }
        const response = await postWithUsername(`${apiUrl}/get-client-data-for-compare`, username);
        const data = await response.json();
        setForm((prevForm) => ({
          ...prevForm,
          client_name: data.client_name,
          client_id: data.client_id,
          job_id: data.job_id,
          srid: data.srid,
          main_table_name: data.main_table_name
        }));
      } catch (error) {
        // handle error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setSelectedFiles(files);
    if (files && files.length > 0) {
      const fileName = files[0].name.toLowerCase();
      setIsLayerNameRequired(fileName.endsWith(".gdb.zip"));
    }
  };

  const handleClearUploaded = (table: string) => {
    localStorage.removeItem(`uploadedCompareFile_${table}`);
    setUploadedLayers((prev) => prev.filter((t) => t !== table));
    setFailedLayers((prev) => prev.filter((t) => t !== table));
    setUploadedFileNames((prev) => {
      const updated = { ...prev };
      delete updated[table];
      return updated;
    });
    // Optionally reset the form if the cleared table was selected
    if (form.create_table_name === table) {
      setForm((prev) => ({ ...prev, create_table_name: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Add this check:
    if (!form.create_table_name) {
      alert("Please select a Dataset Name from the drop-down.");
      return;
    }

    // set the latest pending value to track for failure
    pendingUploadTableRef.current = form.create_table_name;
    // make sure failed layers list is cleared to prevent duplicates and stale values
    setFailedLayers((prev) => prev.filter((t) => t !== form.create_table_name));

    localStorage.setItem(
      `uploadedCompareFile_${form.create_table_name}`,
      Array.from(selectedFiles || []).map(f => f.name).join(", ")
    );

    // Update state immediately
    uploadedFiles = Array.from(selectedFiles || []).map(f => f.name);
    setUploadedLayers(prev =>
      prev.includes(form.create_table_name)
        ? prev
        : [...prev, form.create_table_name]
    );
    setUploadedFileNames(prev => ({
      ...prev,
      [form.create_table_name]: uploadedFiles,
    }));


    setUploading(true);
    setUploadStatus(null);      // Reset status
    setStatusMessage(null);     // Reset message
    setPollingJobId(null);

    try {
      // Upload file, passing job_id and other fields
      const formData = new FormData();
      formData.append("job_id", form.job_id ?? "");
      formData.append("username", userName ?? "");
      formData.append("client_name", form.client_name ?? "");
      formData.append("client_id_passed", form.client_id ?? "");
      formData.append("layer_name", form.layer_name ?? "");
      formData.append("create_table_name", form.create_table_name.toLowerCase() ?? "");
      formData.append("srid", String(form.srid ?? ""));
      formData.append("combine_table_flag", form.combine_table_flag ?? "false");
      formData.append("main_table_name", form.main_table_name ?? "");
      Array.from(selectedFiles).forEach((file) => {
        formData.append("compare_layer_files", file);
      });

      try {
        const response = await fetchWithAuth(`${apiUrl}/load-cof-compare-data`, {
          method: "POST",
          body: formData,
        });
        const data = await response.json();
        if (response.ok && data.status === "processing" && data.id) {
          setPollingJobId(data.id);
          setStatusMessage(data.message);
          setSelectedFiles(null); // Clear selected files
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          // Save uploaded file name to localStorage
          uploadedFiles = Array.from(selectedFiles || []).map(f => f.name);
          const prev = JSON.parse(localStorage.getItem("uploadedCompareFile_") || "{}");
          prev[form.create_table_name] = uploadedFiles;
          localStorage.setItem("uploadedCompareFile_", JSON.stringify(prev));
        } else {
          pendingUploadTableRef.current = null;
          alert(data.message || "Upload failed");
        }
      } catch (err) {
        pendingUploadTableRef.current = null;
        alert("Upload failed");
        console.error(err);
      }
    } finally {
      setUploading(false);
      setForm((prev) => ({ ...prev, create_table_name: "" })); // unlock all buttons
    }
  };

  const readyToUpload = !uploading && !!form.create_table_name && !!selectedFiles && selectedFiles.length > 0;

  const clearSelectedTable = () => {
    setForm((prev) => ({ ...prev, create_table_name: "" }));
    setSelectedFiles(null);
    setIsLayerNameRequired(false);
    setIsCombineBoxShown(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  type TableUploadButtonProps = {
    table: string;
    onUploadClick: (table: string) => void;
    disabled?: boolean;
  };

  const TableUploadButton: React.FC<TableUploadButtonProps> = ({
    table,
    onUploadClick,
    disabled = false,
  }) => (
    <Button
      size="small"
      variant="contained"
      onClick={() => onUploadClick(table)}
      disabled={disabled}
      sx={{
        textTransform: "none",
        minWidth: 132,
        width: 132,
        whiteSpace: "nowrap",
        backgroundColor: "#0373B5",
      }}
    >
      Select Files
    </Button>
  );

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          pt: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          pt: 6,
        }}
      >
        <Typography variant="h6" color="error">
          You must be logged in to access this page.
        </Typography>
      </Box>
    );
  }

  if (!form.client_id || !form.job_id) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          pt: 6,
        }}
      >
        <Typography variant="h6" color="error">
          Please Upload Base Data First
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pt: 6,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>
        Upload Comparison Data
      </Typography>
      <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
        <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
      </svg>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          borderRadius: 1,
          p: 2,
          mb: 1,
          gap: 1,
          maxWidth: 400,
          width: "100%",
        }}
      >
        {/* <CloudUploadIcon color="primary" sx={{ mt: "2px" }} /> */}
        <Typography component="div" sx={{ fontSize: 16 }}>
          Using the below list, upload all relevant zipped (e.g., .zip) files. Only select multiple files if you intend to combine them
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}
          >
            .
            <Tooltip
              title='If you have multiple files for the same dataset (e.g., multiple wetland shapefiles), you can choose to combine them into a single dataset.'
              arrow
            >
              <IconButton
                component="span"
                size="small"
                aria-label="Combine files help"
                sx={{ ml: 0.25, p: 0.25, color: "#5F5F5F" }}
              >
                <HelpOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 720, width: "100%", mb: 2 }}>
        <List dense sx={{ width: "100%" }}>
          {REQUIRED_TABLES.map((table) => (
            <ListItem
              key={table}
              ref={(el) => {
                tableRowRefs.current[table] = el;
              }}
              disableGutters
              sx={{
                display: "grid",
                gridTemplateColumns: "100px 2fr 140px",
                columnGap: 2,
                alignItems: "start",
                py: 1.25,
                borderBottom: "1px solid",
                borderColor: "divider",
                ...(form.create_table_name === table && {
                  bgcolor: "rgba(3, 115, 181, 0.08)",
                }),
              }}
            >
              {/* Column 1: table name */}
              <Typography variant="body2" sx={{ fontWeight: 600, pt: 0.5 }}>
                {DISPLAY_NAMES[table] || table}
              </Typography>

              {/* Column 2: uploaded file names */}
              <Box sx={{ minWidth: 0 }}>
                {uploadedLayers.includes(table) ? (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <CheckCircleIcon sx={{ color: "success.main", mt: "2px", flexShrink: 0 }} />
                    <Tooltip title="Clear uploaded file">
                      <IconButton
                        size="small"
                        onClick={() => handleClearUploaded(table)}
                        sx={{ p: 0.25, mt: "1px", flexShrink: 0, color: "text.secondary" }}
                        aria-label={`Clear uploaded file for ${DISPLAY_NAMES[table] || table}`}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{
                        display: "block",
                        color: "text.primary",
                        lineHeight: 1.35,
                      }}
                    >
                      {(uploadedFileNames[table] || []).map((name, i, arr) => (
                        <React.Fragment key={i}>
                          <span style={{ whiteSpace: "nowrap" }}>{name}</span>
                          {i < arr.length - 1 ? ", " : ""}
                        </React.Fragment>
                      ))}
                    </Typography>
                  </Box>
                ) : (
                  failedLayers.includes(table) ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <HighlightOffIcon sx={{ color: "error.main", fontSize: 16, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: "error.main", pt: 0.5, display: "block" }}>
                        Upload failed
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: "text.secondary", pt: 0.5, display: "block" }}>
                      No file uploaded
                    </Typography>
                  )
                )}
              </Box>

              {/* Column 3: actions */}
              <Box sx={{ display: "flex", flexDirection: "row", gap: 0.75, alignItems: "center", justifyContent: "flex-end" }}>
                <TableUploadButton
                  table={table}
                  disabled={uploading || (!!form.create_table_name && form.create_table_name !== table)}
                  onUploadClick={(selectedTable) => {
                    setForm((prev) => ({ ...prev, create_table_name: selectedTable }));
                    fileInputRef.current?.click();
                  }}
                />
                {form.create_table_name === table && (
                  <Button
                    size="small"
                    color="secondary"
                    onClick={clearSelectedTable}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </ListItem>
          ))}
        </List>
        {/* File Input */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={e => {
            handleFileChange(e);
            // default is false, display box as soon as file length greater than 1
            setIsCombineBoxShown(e.target.files ? e.target.files.length > 1 : false);
          }}
          style={{ display: "none" }}
        />
      </Box>
      <form
        id="compare-upload-form"
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          maxWidth: 400,
          width: "100%",
        }}
      >
        {/* <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
          {isLayerNameRequired
            ? "* Layer Name is required for geodatabases (.gdb.zip files)."
            : "* Layer Name is optional for other file types."}
        </Typography> */}
        {/* <TextField
          select
          name="create_table_name"
          label="Dataset Name"
          value={form.create_table_name}
          onChange={handleInputChange}
          required
          sx={{ mb: 2 }}
        >
          {availableTables.map((table) => (
            <MenuItem key={table} value={table}>
              {table}
            </MenuItem>
          ))}
        </TextField> */}
        {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Add the name of the dataset you are uploading (e.g., "Roadways", "Flowlines", etc.)
        </Typography> */}
        {isCombineBoxShown && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: "primary.main",
                    '&.Mui-checked': {
                      color: "secondary.main",
                    },
                    mr: 1,
                  }}
                  checked={form.combine_table_flag === "true"}
                  /// ... is spread opperator, copying all key values from form and updating combine_table_flag
                  onChange={e =>
                    setForm({ ...form, combine_table_flag: e.target.checked ? "true" : "false" })
                  }
                />
              }
              label="Combine All Uploaded Files?"
              sx={{ ml: 0 }}
            />
            {isCombineBoxShown && (
              <Box
                ref={combineSectionRef}
                sx={{
                  position: "sticky",
                  top: 12,
                  zIndex: 5,
                  backgroundColor: "background.paper",
                  py: 0.5,
                  alignSelf: "flex-start",
                }}
              >
                <Button
                  size="small"
                  onClick={() => setShowCombineHelp((prev) => !prev)}
                  sx={{ textTransform: "none" }}
                >
                  {showCombineHelp ? "Hide" : "Show"} help about combining files
                </Button>


                <Collapse in={showCombineHelp}>
                  <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: 1, mb: 1 }}>
                    <Typography variant="body2">
                      <b>Combining files:</b> Use this option if you want to merge all selected files into a single dataset.
                      <ul>
                        <li>MultiLineString files cannot be combined with other types. Multipolygon and polygon files can be combined with each other.</li>
                        <li>If files are not compatible, the combine may fail.</li>
                        <li>Cannot combine two separate Geodatabases, or Geodatabase layers.</li>
                      </ul>
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}
          </>
        )}
        {/* Hidden Fields */}
        <input type="hidden" name="client_id" value={form.client_id} />
        <input type="hidden" name="client_name" value={form.client_name} />
        <input type="hidden" name="srid" value={form.srid} />
        <input type="hidden" name="main_table_name" value={form.main_table_name} />
      </form>
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          py: 2,
          backgroundColor: "background.paper",
          zIndex: 20,
        }}
      >
        {isLayerNameRequired && (
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 1, px: 2 }}>
            <input
              name="layer_name"
              placeholder="Layer Name"
              value={form.layer_name}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                maxWidth: "300px",
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontFamily: "inherit",
                fontSize: "14px",
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: "0.75rem", textAlign: "center" }}>
              Layer Name is required for geodatabase (.gdb.zip) files
            </Typography>
          </Box>
        )}
        <Button
          type="submit"
          form="compare-upload-form"
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          sx={{
            textTransform: "none",
            width: "fit-content",
            minWidth: "132px",
            backgroundColor: "#0373B5",
            ...(readyToUpload && {
              outline: "3px solid #D97706",
              outlineOffset: "3px",
              "@keyframes uploadPulse": {
                "0%": { boxShadow: "0 0 0 0px rgba(217, 119, 6, 0.5)" },
                "70%": { boxShadow: "0 0 0 8px rgba(217, 119, 6, 0)" },
                "100%": { boxShadow: "0 0 0 0px rgba(217, 119, 6, 0)" },
              },
              animation: "uploadPulse 1.6s ease-in-out infinite",
            }),
          }}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </Box>
      {/* {uploading && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Uploading...
          </Typography>
          <CloudUploadIcon />
        </Box>
      )} */}
      {/* {(uploading || (pollingJobId && (!uploadStatus || uploadStatus === "processing"))) && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {uploading ? "Uploading..." : "Processing..."}
          </Typography>
          <CircularProgress size={20} />
        </Box>
      )}

      {uploadStatus && !pollingJobId && (
        <Box sx={{ mt: 2, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
          <Typography variant="h6">Upload Status</Typography>
          <Typography variant="body2">
            Status: {uploadStatus}
          </Typography>
          <Typography variant="body2">
            Message: {statusMessage}
          </Typography>
        </Box>
      )} */}
      <Dialog
        open={dialogOpen}
        aria-labelledby="upload-status-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="upload-status-title">Upload Status</DialogTitle>
        <DialogContent>
          {(uploading || (pollingJobId && (!uploadStatus || uploadStatus === "processing"))) ? (
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2">
                {uploading ? "Uploading..." : "Processing..."}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2">
                Status: {uploadStatus}
              </Typography>
              <Typography variant="body2">
                Message: {statusMessage}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          {uploadStatus === "completed" && (
            <Button
              onClick={() => setDialogOpen(false)}
              autoFocus
              variant="contained"
            >
              Okay
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadService;